import { FC } from "react";

import Stack from "@mui/material/Stack";

import { useRateRenewalPrices } from "../../hooks/useRateRenewalPrices";
import { AmountField } from "../../RateFields/AmountField/AmountField";
import { AutoRenewalCheckbox } from "../../RateFields/AutoRenewalCheckbox/AutoRenewalCheckbox";
import { DescriptionField } from "../../RateFields/DescriptionField/DescriptionField";
import { HiddenChip } from "../../RateFields/HiddenChip/HiddenChip";
import { NameField } from "../../RateFields/NameField/NameField";
import { useRenewalPriceChange } from "../../RateFields/RenewalPriceChangeInfobox/helpers/useRenewalPriceChange";
import { RenewalPriceChangeInfobox } from "../../RateFields/RenewalPriceChangeInfobox/RenewalPriceChangeInfobox";
import { ValidityAtDatePicker } from "../../RateFields/ValidityAtDatePicker/ValidityAtDatePicker";
import { ValidityPeriodField } from "../../RateFields/ValidityPeriodField/ValidityPeriodField";
import { ValidityRenewalModeField } from "../../RateFields/ValidityRenewalModeField/ValidityRenewalModeField";
import { VisibilityToggleField } from "../../RateFields/VisibilityToggleField/VisibilityToggleField";
import { ExtrasSection } from "../../shared/ExtrasSection/ExtrasSection";
import { ExtrasSummary } from "../../shared/ExtrasSummary/ExtrasSummary";
import { CategoriesRow } from "../../shared/RateOptions/OptionRows/CategoriesRow";
import { ScheduleRow } from "../../shared/RateOptions/OptionRows/ScheduleRow/ScheduleRow";
import { PayWhatYouCanRow } from "../../shared/RateOptions/OptionRows/PayWhatYouCanRow";
import { TaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/TaxReceiptRow";
import { useShowTaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/useShowTaxReceiptRow";
import { TaxRow } from "../../shared/RateOptions/OptionRows/TaxRow/TaxRow";
import { useShowTaxRow } from "../../shared/RateOptions/OptionRows/TaxRow/useShowTaxRow";
import { RateOptionsGroup } from "../../shared/RateOptions/RateOptionsGroup";
import { RateCardShell } from "../../shared/RateCardShell/RateCardShell";
import { RateStatusChip } from "../../shared/RateStatusChip/RateStatusChip";

import { useTranslate } from "@/common/hooks/useTranslate";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useFamilyGroupMembershipFlag } from "@/common/hooks/useFamilyGroupMembershipFlag";
import { GroupMembershipRow } from "./GroupMembershipRow/GroupMembershipRow";
import { GroupMembershipSuggestion } from "./GroupMembershipSuggestion/GroupMembershipSuggestion";

interface MembershipRateProps {
  totalCardNumber: number;
  shouldDisplayDeleteButton?: boolean;
  shouldConfirmDelete?: boolean;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  questionsArchivedOnDelete?: string[];
}

export const MembershipRate: FC<MembershipRateProps> = (props) => {
  const { t } = useTranslate();
  const showTaxReceiptRow = useShowTaxReceiptRow();
  const showTaxRow = useShowTaxRow();
  const { organization } = useCurrentUserContext();
  const { isActive: groupMembershipEnabled } = useFamilyGroupMembershipFlag(organization?.id);
  const renewalPriceChange = useRenewalPriceChange();
  const { lastPaidPrices, isLoading: isRenewalPricesLoading } = useRateRenewalPrices();
  const hasAutoRenewingMembers = lastPaidPrices.length > 0;

  return (
    <RateCardShell
      {...props}
      statusChip={
        <>
          <RateStatusChip />
          <HiddenChip />
        </>
      }
      visibilityAction={
        <VisibilityToggleField
          hideLabel={t("dashboard", "ticketing.membershipRate.hide")}
          showLabel={t("dashboard", "ticketing.membershipRate.show")}
        />
      }
      isDeleteDisabled={hasAutoRenewingMembers || isRenewalPricesLoading}
      deleteDisabledTooltip={
        hasAutoRenewingMembers ? t("dashboard", "ticketing.membershipRate.deleteDisabled") : undefined
      }
    >
      <Stack spacing={1.5}>
        <NameField />
        {groupMembershipEnabled && <GroupMembershipSuggestion field="name" />}
        <Stack spacing={0.5}>
          <AmountField />
          {renewalPriceChange && <RenewalPriceChangeInfobox {...renewalPriceChange} />}
        </Stack>
        <DescriptionField />
        {groupMembershipEnabled && <GroupMembershipSuggestion field="description" />}

        <Stack spacing={1}>
          <ValidityPeriodField />
          <AutoRenewalCheckbox />
          <ValidityRenewalModeField />
          <ValidityAtDatePicker />
        </Stack>

        <ExtrasSection
          mode="toggle"
          variant="options-panel"
          optionsLabel={t("dashboard", "ticketing.membershipOptions")}
          collapsedSummary={<ExtrasSummary />}
        >
          <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.capacity")}>
            <ScheduleRow />
          </RateOptionsGroup>
          <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.ticketType")}>
            <PayWhatYouCanRow />
            {groupMembershipEnabled && <GroupMembershipRow />}
            <CategoriesRow />
          </RateOptionsGroup>
          {(showTaxReceiptRow || showTaxRow) && (
            <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.taxesAndReceipts")}>
              {showTaxReceiptRow && <TaxReceiptRow />}
              {showTaxRow && <TaxRow />}
            </RateOptionsGroup>
          )}
        </ExtrasSection>
      </Stack>
    </RateCardShell>
  );
};
